import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, List, ListItemButton, ListItemText, MenuItem,
  Stack, Tab, Tabs, TextField, Typography,
} from "@mui/material";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, LineChart, Line } from "recharts";
import { BarChart3, LayoutDashboard, Plus, Pencil, Trash2, ArrowLeft, ArrowRight, Download } from "lucide-react";
import { CardHeader, EmptyState, GlassCard, PageHeader, T, TYPE, CHART, SERIES, ChartTooltip, formatRelative, primaryButtonSx, ghostButtonSx } from "../user/complaints/shared";
import { pm, errorMessage, saveBlob } from "./api";
import { PmPage, usePm } from "./PmContext";
import { TypeIcon, StatusPill, Progress, ACTIVITY_TEXT } from "./ui";
import { Select, fieldSx } from "./inputs";
import PmTopBar from "./PmTopBar";
import { ReportView } from "./project/ProjectReports";

const WIDGETS = {
  assigned_to_me: "Assigned to me", overdue: "Overdue issues", recent_activity: "Recent activity", filter_results: "Saved filter results",
  issue_stats: "Issue statistics", project_progress: "Project progress", sprint_progress: "Sprint progress", burndown: "Burndown", velocity: "Velocity",
  workload: "Workload", bugs: "Bugs", completion: "Completion trend", time: "Time logged", release: "Release progress",
};
const NEEDS_PROJECT = ["project_progress", "sprint_progress", "burndown", "velocity", "release"];

const WidgetBody = ({ dashboard, widget }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    setData(null);
    setError("");
    pm.widget(dashboard._id, widget._id).then(setData).catch((err) => setError(errorMessage(err)));
  }, [dashboard._id, widget._id, JSON.stringify(widget.config), JSON.stringify(dashboard.filters)]); // eslint-disable-line react-hooks/exhaustive-deps
  if (error) return <Typography sx={{ fontSize: TYPE.small, color: T.inkMuted, p: 2 }}>{error}</Typography>;
  if (!data) return <Box sx={{ p: 3, textAlign: "center" }}><CircularProgress size={18} /></Box>;
  const issues = (rows) => rows.map((i) => (
    <Stack key={i._id} direction="row" spacing={1} alignItems="center" sx={{ px: 2, py: 0.75, cursor: "pointer", "&:hover": { bgcolor: T.surfaceMuted } }} onClick={() => navigate(`/projects/${i.key.split("-")[0]}/issues/${i.key}`)}>
      <TypeIcon typeId={i.typeId} size={13} /><Typography sx={{ fontSize: TYPE.micro, fontWeight: 700 }}>{i.key}</Typography>
      <Typography noWrap sx={{ fontSize: TYPE.small, flexGrow: 1 }}>{i.summary}</Typography><StatusPill statusId={i.statusId} />
    </Stack>
  ));
  switch (widget.type) {
    case "assigned_to_me": case "overdue": case "filter_results":
      return data.length ? <Box sx={{ maxHeight: 320, overflowY: "auto" }}>{issues(data)}</Box> : <Typography sx={{ p: 2, fontSize: TYPE.small, color: T.inkMuted }}>Nothing.</Typography>;
    case "recent_activity":
      return <Box sx={{ p: 2, maxHeight: 320, overflowY: "auto" }}>{data.map((a) => <Box key={a._id} sx={{ py: 0.5 }}><Typography sx={{ fontSize: TYPE.small }}>{ACTIVITY_TEXT(a)}</Typography><Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{formatRelative(a.createdAt)}</Typography></Box>)}</Box>;
    case "issue_stats":
      return (
        <Box sx={{ height: 240, p: 1.5 }}>
          <ResponsiveContainer>
            <BarChart data={data.byStatus}><CartesianGrid stroke={CHART.grid} vertical={false} /><XAxis dataKey="name" tick={CHART.tick} {...CHART.axisProps} /><YAxis tick={CHART.tick} {...CHART.axisProps} width={30} allowDecimals={false} /><RTooltip content={<ChartTooltip />} /><Bar dataKey="count" name="Issues" fill={SERIES[0]} radius={[4, 4, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </Box>
      );
    case "project_progress":
      return <Box sx={{ p: 2 }}><Progress value={data.progressPct} label={`${data.totals.done}/${data.totals.total} done · ${data.totals.overdue} overdue`} /></Box>;
    case "sprint_progress":
      return data.sprint ? <Box sx={{ p: 2 }}><Typography sx={{ fontSize: TYPE.small, fontWeight: 700, mb: 1 }}>{data.sprint.name}</Typography><Progress value={data.sprint.pct} label={`${data.sprint.done}/${data.sprint.issues} issues`} color="#12B76A" /></Box> : <Typography sx={{ p: 2, fontSize: TYPE.small, color: T.inkMuted }}>No active sprint.</Typography>;
    case "burndown":
      return (
        <Box sx={{ height: 240, p: 1.5 }}>
          <ResponsiveContainer>
            <LineChart data={data.series}><CartesianGrid stroke={CHART.grid} vertical={false} /><XAxis dataKey="day" tick={CHART.tick} {...CHART.axisProps} tickFormatter={(d) => d.slice(5)} /><YAxis tick={CHART.tick} {...CHART.axisProps} width={30} /><RTooltip content={<ChartTooltip />} /><Line dataKey="ideal" stroke={T.inkMuted} strokeDasharray="4 4" dot={false} /><Line dataKey="remaining" stroke={SERIES[4]} strokeWidth={2} /></LineChart>
          </ResponsiveContainer>
        </Box>
      );
    case "velocity":
      return (
        <Box sx={{ height: 240, p: 1.5 }}>
          <ResponsiveContainer>
            <BarChart data={data.sprints}><CartesianGrid stroke={CHART.grid} vertical={false} /><XAxis dataKey="name" tick={CHART.tick} {...CHART.axisProps} /><YAxis tick={CHART.tick} {...CHART.axisProps} width={30} /><RTooltip content={<ChartTooltip />} /><Bar dataKey="committed" fill={SERIES[1]} /><Bar dataKey="completed" fill={SERIES[2]} /></BarChart>
          </ResponsiveContainer>
        </Box>
      );
    case "workload":
      return <Box sx={{ p: 2 }}>{data.rows.slice(0, 8).map((r) => <Stack key={String(r.id)} direction="row" justifyContent="space-between" sx={{ py: 0.4 }}><Typography sx={{ fontSize: TYPE.small }}>{r.name}</Typography><Typography sx={{ fontSize: TYPE.small, fontWeight: 700 }}>{r.open} open{r.overdue ? ` · ${r.overdue} overdue` : ""}</Typography></Stack>)}</Box>;
    case "bugs":
      return <Box sx={{ p: 2 }}><Typography sx={{ fontSize: "1.6rem", fontWeight: 700 }}>{data.openCount}</Typography><Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary }}>open bugs · avg age {data.averageOpenAgeDays ?? "—"} days</Typography></Box>;
    case "completion":
      return (
        <Box sx={{ height: 240, p: 1.5 }}>
          <ResponsiveContainer><BarChart data={data.rows}><CartesianGrid stroke={CHART.grid} vertical={false} /><XAxis dataKey="period" tick={CHART.tick} {...CHART.axisProps} /><YAxis tick={CHART.tick} {...CHART.axisProps} width={30} /><RTooltip content={<ChartTooltip />} /><Bar dataKey="issues" fill={SERIES[2]} radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer>
        </Box>
      );
    case "time":
      return <Box sx={{ p: 2 }}><Typography sx={{ fontSize: "1.4rem", fontWeight: 700 }}>{data.totalHours}h</Typography>{data.rows.slice(0, 6).map((r) => <Stack key={String(r.id)} direction="row" justifyContent="space-between"><Typography sx={{ fontSize: TYPE.small }}>{r.name}</Typography><Typography sx={{ fontSize: TYPE.small }}>{r.hours}h</Typography></Stack>)}</Box>;
    case "release":
      return <Box sx={{ p: 2 }}>{data.versions.filter((v) => v.status === "unreleased").slice(0, 5).map((v) => <Box key={v._id} sx={{ mb: 1 }}><Progress value={v.progressPct} label={v.name} /></Box>)}</Box>;
    default:
      return null;
  }
};

const WidgetDialog = ({ state, projects, filters, onClose, onSave }) => {
  const [w, setW] = useState(null);
  useEffect(() => {
    if (state) setW({ type: "assigned_to_me", title: "", config: {}, layout: { w: 6, h: 4 }, ...state });
  }, [state]);
  if (!state || !w) return null;
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{state._id ? "Edit widget" : "Add widget"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Select label="Widget" value={w.type} onChange={(type) => setW({ ...w, type })} options={Object.entries(WIDGETS).map(([v, l]) => ({ value: v, label: l }))} />
          <TextField size="small" label="Title (optional)" value={w.title} onChange={(e) => setW({ ...w, title: e.target.value })} sx={fieldSx} />
          <Select label={NEEDS_PROJECT.includes(w.type) ? "Project *" : "Project (optional)"} value={w.config.projectId} onChange={(projectId) => setW({ ...w, config: { ...w.config, projectId } })} allowEmpty={!NEEDS_PROJECT.includes(w.type)} emptyLabel="All projects" options={projects.map((p) => ({ value: p._id, label: `${p.key} · ${p.name}` }))} />
          {w.type === "filter_results" && <Select label="Saved filter" value={w.config.filterId} onChange={(filterId) => setW({ ...w, config: { ...w.config, filterId } })} options={filters.map((f) => ({ value: f._id, label: f.name }))} />}
          <Select label="Width" value={w.layout.w} onChange={(v) => setW({ ...w, layout: { ...w.layout, w: Number(v) } })} options={[[4, "Third"], [6, "Half"], [8, "Two thirds"], [12, "Full"]].map(([v, l]) => ({ value: v, label: l }))} />
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cancel</Button><Button variant="contained" sx={primaryButtonSx} disabled={NEEDS_PROJECT.includes(w.type) && !w.config.projectId} onClick={() => onSave(w)}>Save</Button></DialogActions>
    </Dialog>
  );
};

const Dashboards = () => {
  const ctx = usePm();
  const { id } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [dash, setDash] = useState(null);
  const [projects, setProjects] = useState([]);
  const [filters, setFilters] = useState([]);
  const [widgetEdit, setWidgetEdit] = useState(null);
  const [meta, setMeta] = useState(null);
  const loadList = useCallback(() => pm.dashboards().then((r) => setList(r.data)).catch(() => setList([])), []);
  useEffect(() => {
    loadList();
    pm.projects().then(setProjects).catch(() => {});
    pm.filters().then(setFilters).catch(() => {});
  }, [loadList]);
  useEffect(() => {
    if (id) pm.dashboard(id).then(setDash).catch((err) => ctx.fail(err));
    else setDash(null);
  }, [id, ctx]);
  // Only one dashboard: open it directly instead of a one-row list (New dashboard is also in its header).
  const lone = !id && list?.length === 1 ? list[0]._id : null;
  useEffect(() => {
    if (lone) navigate(`/pm/dashboards/${lone}`, { replace: true });
  }, [lone, navigate]);
  const newDashboard = () => setMeta({ name: "", visibility: "private" });
  const createDashboard = async (m) => {
    try {
      const d = await pm.saveDashboard(null, { ...m, widgets: [{ type: "assigned_to_me", layout: { w: 6, h: 4 } }, { type: "issue_stats", layout: { w: 6, h: 4 } }] });
      setMeta(null);
      loadList();
      navigate(`/pm/dashboards/${d._id}`);
    } catch (err) {
      ctx.fail(err);
    }
  };

  const save = async (patch) => {
    try {
      const saved = await pm.saveDashboard(dash._id, patch);
      setDash({ ...saved, mine: true });
      loadList();
    } catch (err) {
      ctx.fail(err);
    }
  };

  if (!id) {
    return (
      <Box>
        <Stack direction="row" sx={{ mb: 2 }}><Box sx={{ flexGrow: 1 }} /><Button variant="contained" sx={primaryButtonSx} startIcon={<Plus size={14} />} onClick={newDashboard}>New dashboard</Button></Stack>
        {!list || lone ? <CircularProgress size={22} /> : !list.length ? (
          <GlassCard><EmptyState icon={LayoutDashboard} title="No dashboards yet" description="Build a dashboard from widgets: your issues, sprint progress, burndown, workload, bugs, saved filters and more." /></GlassCard>
        ) : (
          <GlassCard><List>{list.map((d) => <ListItemButton key={d._id} onClick={() => navigate(`/pm/dashboards/${d._id}`)}><ListItemText primary={d.name} secondary={`${d.widgets.length} widgets · ${d.visibility}${d.mine ? "" : ` · by ${d.owner.name}`}`} /></ListItemButton>)}</List></GlassCard>
        )}
        <MetaDialog state={meta} projects={projects} onClose={() => setMeta(null)} onSave={createDashboard} />
      </Box>
    );
  }
  if (!dash) return <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress size={24} /></Box>;
  const moveWidget = (i, dir) => {
    const w = [...dash.widgets];
    const j = i + dir;
    if (j < 0 || j >= w.length) return;
    [w[i], w[j]] = [w[j], w[i]];
    save({ widgets: w });
  };
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        {list?.length > 1 && <Button size="small" onClick={() => navigate("/pm/dashboards")}>All dashboards</Button>}
        <Typography sx={{ fontWeight: 700, fontSize: TYPE.cardTitle }}>{dash.name}</Typography>
        <Chip size="small" label={dash.visibility} sx={{ height: 20 }} />
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" variant="outlined" sx={ghostButtonSx} startIcon={<Plus size={13} />} onClick={newDashboard}>New dashboard</Button>
        {dash.mine && (
          <>
            <Button size="small" variant="outlined" sx={ghostButtonSx} startIcon={<Pencil size={13} />} onClick={() => setMeta(dash)}>Settings</Button>
            <Button size="small" variant="contained" sx={primaryButtonSx} startIcon={<Plus size={13} />} onClick={() => setWidgetEdit({})}>Widget</Button>
            <IconButton size="small" onClick={async () => { if (window.confirm("Delete this dashboard?")) { await pm.deleteDashboard(dash._id); loadList(); navigate("/pm/dashboards"); } }}><Trash2 size={15} /></IconButton>
          </>
        )}
      </Stack>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(12, minmax(0,1fr))" }, gap: 2 }}>
        {dash.widgets.map((w, i) => (
          <GlassCard key={w._id} sx={{ gridColumn: { md: `span ${w.layout?.w || 6}` }, minWidth: 0 }}>
            <CardHeader title={w.title || WIDGETS[w.type]} subtitle={w.config?.projectId ? projects.find((p) => p._id === w.config.projectId)?.name : undefined}
              action={dash.mine && (
                <Stack direction="row">
                  <IconButton size="small" onClick={() => moveWidget(i, -1)}><ArrowLeft size={13} /></IconButton>
                  <IconButton size="small" onClick={() => moveWidget(i, 1)}><ArrowRight size={13} /></IconButton>
                  <IconButton size="small" onClick={() => setWidgetEdit({ ...w, _index: i })}><Pencil size={13} /></IconButton>
                  <IconButton size="small" onClick={() => save({ widgets: dash.widgets.filter((_, j) => j !== i) })}><Trash2 size={13} /></IconButton>
                </Stack>
              )} />
            <WidgetBody dashboard={dash} widget={w} />
          </GlassCard>
        ))}
      </Box>
      <WidgetDialog state={widgetEdit} projects={projects} filters={filters} onClose={() => setWidgetEdit(null)}
        onSave={(w) => { const { _index, ...clean } = w; save({ widgets: _index >= 0 && _index !== undefined ? dash.widgets.map((x, j) => (j === _index ? clean : x)) : [...dash.widgets, clean] }); setWidgetEdit(null); }} />
      <MetaDialog state={meta} projects={projects} onClose={() => setMeta(null)} onSave={(m) => { if (!meta?._id) { createDashboard(m); return; } save({ name: m.name, visibility: m.visibility, teamId: m.teamId || null, projectId: m.projectId || null }); setMeta(null); }} />
    </Box>
  );
};

const MetaDialog = ({ state, projects, onClose, onSave }) => {
  const ctx = usePm();
  const [m, setM] = useState(null);
  useEffect(() => {
    if (state) setM({ name: state.name || "", visibility: state.visibility || "private", teamId: state.teamId || null, projectId: state.projectId || null });
  }, [state]);
  if (!state || !m) return null;
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Dashboard</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField size="small" label="Name" value={m.name} onChange={(e) => setM({ ...m, name: e.target.value })} sx={fieldSx} autoFocus />
          <Select label="Who can see it" value={m.visibility} onChange={(visibility) => setM({ ...m, visibility })} options={[["private", "Only me"], ["team", "A team"], ["project", "Members of a project"], ["organization", "Everyone in the workspace"]].map(([v, l]) => ({ value: v, label: l }))} />
          {m.visibility === "team" && <Select label="Team" value={m.teamId} onChange={(teamId) => setM({ ...m, teamId })} options={ctx.teams.map((t) => ({ value: t._id, label: t.name }))} />}
          {m.visibility === "project" && <Select label="Project" value={m.projectId} onChange={(projectId) => setM({ ...m, projectId })} options={projects.map((p) => ({ value: p._id, label: p.name }))} />}
          <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>Viewers only ever see data from projects they can access.</Typography>
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cancel</Button><Button variant="contained" sx={primaryButtonSx} disabled={!m.name} onClick={() => onSave(m)}>Save</Button></DialogActions>
    </Dialog>
  );
};

const REPORTS = [["issues", "Issues"], ["workload", "Workload"], ["time", "Time tracking"], ["bugs", "Bugs"], ["completion", "Completion"], ["velocity", "Velocity"], ["release", "Releases"]];

const Reports = () => {
  const ctx = usePm();
  const [name, setName] = useState("issues");
  const [projects, setProjects] = useState([]);
  const [params, setParams] = useState({ projectId: "", from: new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10), to: new Date().toISOString().slice(0, 10), interval: "week", groupBy: "user" });
  useEffect(() => {
    pm.projects().then((rows) => {
      setProjects(rows);
      // Only one project: report on it directly instead of "All my projects".
      if (rows.length === 1) setParams((x) => (x.projectId ? x : { ...x, projectId: rows[0]._id }));
    }).catch(() => {});
  }, []);
  const needsProject = ["velocity", "release"].includes(name);
  const q = Object.fromEntries(Object.entries(params).filter(([, v]) => v));
  return (
    <Box>
      <Tabs value={name} onChange={(_, v) => setName(v)} variant="scrollable" sx={{ mb: 2, minHeight: 36, "& .MuiTab-root": { minHeight: 36, textTransform: "none", fontSize: TYPE.small } }}>
        {REPORTS.map(([k, l]) => <Tab key={k} value={k} label={l} />)}
      </Tabs>
      <Stack direction="row" spacing={1.25} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap alignItems="center">
        <TextField select size="small" label="Project" value={params.projectId} onChange={(e) => setParams({ ...params, projectId: e.target.value })} sx={{ ...fieldSx, minWidth: 200 }}>
          {!needsProject && <MenuItem value="">All my projects</MenuItem>}
          {projects.map((p) => <MenuItem key={p._id} value={p._id}>{p.key} · {p.name}</MenuItem>)}
        </TextField>
        {["issues", "time", "completion"].includes(name) && (
          <>
            <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={params.from} onChange={(e) => setParams({ ...params, from: e.target.value })} sx={fieldSx} />
            <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={params.to} onChange={(e) => setParams({ ...params, to: e.target.value })} sx={fieldSx} />
          </>
        )}
        {name === "time" && <TextField select size="small" label="Group by" value={params.groupBy} onChange={(e) => setParams({ ...params, groupBy: e.target.value })} sx={{ ...fieldSx, minWidth: 120 }}>{["user", "project", "issue", "sprint"].map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}</TextField>}
        {name === "completion" && <TextField select size="small" label="Interval" value={params.interval} onChange={(e) => setParams({ ...params, interval: e.target.value })} sx={{ ...fieldSx, minWidth: 110 }}>{["day", "week", "month"].map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}</TextField>}
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" startIcon={<Download size={14} />} onClick={async () => { try { saveBlob(await pm.reportCsv(name, q), `pm-${name}.csv`); } catch (err) { ctx.fail(err, "Export failed"); } }}>Export CSV</Button>
      </Stack>
      {needsProject && !params.projectId ? <Alert severity="info">Choose a project for this report.</Alert> : <ReportView name={name} params={q} project={params.projectId ? projects.find((p) => p._id === params.projectId) : null} />}
    </Box>
  );
};

const Inner = ({ mode }) => {
  const navigate = useNavigate();
  return (
    <>
      <PmTopBar />
      <PageHeader
        icon={mode === "dashboards" ? LayoutDashboard : BarChart3}
        title={mode === "dashboards" ? "Project Dashboards" : "Project Reports"}
        subtitle={mode === "dashboards" ? "Customizable KPI widgets and real-time project metrics across your workspace" : "Comprehensive delivery metrics, workload distribution, burndown trends, and velocity tracking"}
        actions={<Stack direction="row" spacing={1}><Button size="small" variant={mode === "reports" ? "contained" : "outlined"} sx={mode === "reports" ? primaryButtonSx : ghostButtonSx} onClick={() => navigate("/pm/reports")}>Reports</Button><Button size="small" variant={mode === "dashboards" ? "contained" : "outlined"} sx={mode === "dashboards" ? primaryButtonSx : ghostButtonSx} onClick={() => navigate("/pm/dashboards")}>Dashboards</Button></Stack>}
      />
      {mode === "dashboards" ? <Dashboards /> : <Reports />}
    </>
  );
};

const PmReports = ({ mode = "reports" }) => (
  <PmPage>
    <Inner mode={mode} />
  </PmPage>
);

export default PmReports;
